import type { StoryCtx } from './ctx.js';
import { categoryOf, frameSize, groupRankAtEnd, groupsOfType, growthShift, layoutLibrary, newCategoryFrame, orderedGroups, slotInGroup, type PlacedEntity } from './libraryLayout.js';
import { entityTitle } from './narrative.js';
import { keysOf, refIndex, remapRefs, uniqueRefKey } from './refs.js';
import type { Entity, FieldDef, FieldValue, LibraryGroup, NodeType } from './types.js';

/**
 * Import in die Bibliothek: Einträge einer anderen Geschichte als Kopien übernehmen (PLAN.md A101). Rein – der Plan sagt, was anzulegen
 * und zu ändern ist; der Server führt ihn in einer Transaktion aus. Die Kopien sind eigenständig: Änderungen an ihnen berühren die
 * Originale nicht (und umgekehrt), Bilder werden mitkopiert.
 */
export interface LibraryImportPlan {
  /** die Kopien: neue IDs, freie Schlüssel, Felder der Zielklasse, Lage in ihrer Kategorie (ohne Kategorie: noch nicht platziert) */
  entities: Entity[];
  /** übersprungen: In der Zielklasse gibt es schon einen Eintrag mit diesem Namen (`existingId`) */
  skipped: { id: string; title: string; existingId: string }[];
  /** neue Klassen: eigene Klassen der Quelle, die es hier noch nicht gibt */
  types: NodeType[];
  /** vorhandene Klassen, denen Felder fehlten: ihre neue, vollständige Feldliste */
  typeFields: { typeId: string; fields: FieldDef[] }[];
  /** neue Kategorien (Name und Farbe wie in der Quelle) */
  groups: LibraryGroup[];
  /** vorhandene Kategorien, die für die Kopien wachsen oder unter einer wachsenden nachrücken */
  groupChanges: { id: string; y: number; w: number; h: number }[];
  /** vorhandene Karten, die unter einer wachsenden Kategorie nachrücken */
  entityMoves: { id: string; y: number }[];
  /** zu kopierende Bilder: ID in der Quelle → neue ID */
  assets: { from: string; to: string }[];
  /** je Kopie (`id`) ihr Original in der Quelle (`from`) */
  origins: { id: string; from: string }[];
}

const norm = (s: string): string => s.trim().toLocaleLowerCase('de');
const isLibraryType = (t: NodeType | undefined): t is NodeType => t?.category === 'object' || t?.category === 'place';
const filled = (v: FieldValue | undefined): v is string | number => v !== null && v !== undefined && v !== '';
const unique = <T>(xs: T[]): T[] => [...new Set(xs)];
/** Name eines Eintrags (Titelfeld seiner Klasse), leer ohne */
const nameOf = (ctx: StoryCtx, e: Entity): string => String(e.fields[ctx.types.get(e.typeId)?.titleKey ?? ''] ?? '').trim();
/** Ein Feld der Quelle passt zu einem der Zielklasse: gleiche Art, und eine feste Auswahl dort umfasst die der Quelle. */
const fits = (from: FieldDef, to: FieldDef): boolean => from.type === to.type && (!to.options || (!!from.options && from.options.every((o) => to.options!.includes(o))));

/** Feldschlüssel, der in `taken` noch nicht vorkommt (höchstens 40 Zeichen, bei Bedarf mit angehängter Zahl). */
function freeFieldKey(key: string, taken: ReadonlySet<string>): string {
  if (!taken.has(key)) return key;
  for (let i = 2; ; i++) {
    const suffix = `_${i}`;
    const candidate = `${key.slice(0, 40 - suffix.length)}${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/** Klasse der Zielgeschichte für Einträge der Klasse `source`: gleicher Name und gleiche Art (Objekt bzw. Ort), sonst null. */
export function matchingType(target: StoryCtx, source: Pick<NodeType, 'name' | 'category'>): NodeType | null {
  return target.story.types.find((t) => isLibraryType(t) && t.category === source.category && norm(t.name) === norm(source.name)) ?? null;
}

/** Eintrag der Zielgeschichte, den es schon gibt: in der Klasse zu `type` (`matchingType`) mit dem Namen `name`. Ohne Namen nie. */
export function existingNamed(target: StoryCtx, type: Pick<NodeType, 'name' | 'category'>, name: string): Entity | null {
  const into = matchingType(target, type);
  const wanted = norm(name);
  if (!into || wanted === '') return null;
  return target.story.entities.find((x) => x.typeId === into.id && norm(nameOf(target, x)) === wanted) ?? null;
}

/** Eintrag der Zielgeschichte, den es zu `e` (aus `source`) schon gibt: gleiche Klasse und gleicher Name. */
export function existingEntry(target: StoryCtx, source: StoryCtx, e: Entity): Entity | null {
  const type = source.types.get(e.typeId);
  return type ? existingNamed(target, type, nameOf(source, e)) : null;
}

/**
 * Plant den Import der Einträge `entityIds` aus `source` in `target`:
 *  - Klassen: gleichnamige Klasse (Person, Ort, … oder eigene); fehlt eine eigene Klasse, kommt eine Kopie dazu. Felder, die der Zielklasse
 *    fehlen, werden ergänzt, sofern eine Kopie darin etwas stehen hat (gleicher Schlüssel oder gleiche Bezeichnung = dasselbe Feld).
 *  - Doppelte: Gibt es in der Zielklasse schon einen Eintrag mit demselben Namen, wird er übersprungen.
 *  - Schlüssel: der alte, wenn er frei ist, sonst mit Zahl („anna2“); weitere Bezeichnungen, soweit frei.
 *  - Verweise in Texten zeigen auf die Kopien bzw. bei Übersprungenen auf den vorhandenen Eintrag; Verweise auf alles andere werden zu
 *    normalem Text.
 *  - Kategorien: die gleichnamige der Zielklasse, sonst eine neue (Name und Farbe der Quelle, hinter den vorhandenen); die Karte kommt auf
 *    einen freien Platz darin – wie beim Hineinlegen in der Bibliothek, der Rahmen wächst bei Bedarf.
 *  - Bilder (Titelbild, Bildfelder, Standardbild neuer Klassen) werden kopiert; `sourceAssets` sind die Bilder der Quelle.
 */
export function planLibraryImport(source: StoryCtx, target: StoryCtx, entityIds: readonly string[], sourceAssets: ReadonlySet<string>, newId: () => string): LibraryImportPlan {
  const picked = unique([...entityIds])
    .map((id) => source.entities.get(id))
    .filter((e): e is Entity => !!e && isLibraryType(source.types.get(e.typeId)));
  const skipped: LibraryImportPlan['skipped'] = [];
  const existing = new Map<string, Entity>();
  const copying: Entity[] = [];
  for (const e of picked) {
    const there = existingEntry(target, source, e);
    if (there) {
      existing.set(e.id, there);
      skipped.push({ id: e.id, title: nameOf(source, e), existingId: there.id });
    } else copying.push(e);
  }

  // --- Bilder: jedes Bild der Quelle einmal ---
  const assetIds = new Map<string, string>();
  const asset = (id: string): string | null => {
    if (!sourceAssets.has(id)) return null;
    let to = assetIds.get(id);
    if (!to) {
      to = newId();
      assetIds.set(id, to);
    }
    return to;
  };

  // --- Klassen und Felder ---
  const types: NodeType[] = [];
  const typeOf = new Map<string, NodeType>();
  const fieldOf = new Map<string, Map<string, FieldDef>>();
  const fieldLists = new Map<string, FieldDef[]>();
  const names = new Set(target.story.types.map((t) => norm(t.name)));
  let sort = target.story.types.reduce((m, t) => Math.max(m, t.sort + 1), 0);
  for (const src of unique(copying.map((e) => source.types.get(e.typeId)!))) {
    const match = matchingType(target, src);
    if (!match) {
      let name = src.name;
      for (let i = 2; names.has(norm(name)); i++) name = `${src.name} ${i}`;
      names.add(norm(name));
      const fields = src.fields.filter((f) => f.type !== 'ref');
      const placeholderId = src.placeholderId ? asset(src.placeholderId) : null;
      const created: NodeType = { ...src, id: newId(), name, builtin: false, textual: false, fields, placeholderId, sort: sort++ };
      types.push(created);
      typeOf.set(src.id, created);
      fieldOf.set(src.id, new Map(fields.map((f) => [f.key, f])));
      continue;
    }
    typeOf.set(src.id, match);
    const fields = fieldLists.get(match.id) ?? [...match.fields];
    const map = new Map<string, FieldDef>();
    const used = new Set<string>();
    const link = (from: FieldDef, to: FieldDef): void => {
      map.set(from.key, to);
      used.add(to.key);
    };
    // Titel, Hover-Text und Titelbild entsprechen einander, auch unter anderem Schlüssel
    for (const role of ['titleKey', 'hoverKey', 'imageKey'] as const) {
      const from = src.fields.find((f) => f.key === src[role]);
      const to = fields.find((f) => f.key === match[role]);
      if (from && to && !used.has(to.key) && fits(from, to)) link(from, to);
    }
    for (const f of src.fields) {
      if (map.has(f.key) || f.type === 'ref') continue;
      const same = fields.find((d) => !used.has(d.key) && d.key === f.key && fits(f, d)) ?? fields.find((d) => !used.has(d.key) && norm(d.label) === norm(f.label) && fits(f, d));
      if (same) {
        link(f, same);
        continue;
      }
      if (!copying.some((e) => e.typeId === src.id && filled(e.fields[f.key]))) continue;
      const def: FieldDef = { ...f, key: freeFieldKey(f.key, new Set(fields.map((d) => d.key))) };
      fields.push(def);
      link(f, def);
    }
    if (fields.length > match.fields.length) fieldLists.set(match.id, fields);
    fieldOf.set(src.id, map);
  }

  // --- Schlüssel der Kopien ---
  const taken = new Set(target.story.entities.flatMap(keysOf));
  const copies = new Map<string, Entity>();
  for (const e of copying) {
    const refKey = uniqueRefKey(taken, e.refKey);
    taken.add(refKey);
    copies.set(e.id, { id: newId(), typeId: typeOf.get(e.typeId)!.id, fields: {}, refKey, terms: [...(e.terms ?? [])], x: null, y: null });
  }

  // --- Felder der Kopien; Verweise auf die Kopien bzw. vorhandene Einträge, sonst normaler Text ---
  const byKey = refIndex(source);
  const to = (key: string): string | null => {
    const e = byKey.get(key);
    if (!e) return null;
    const copy = copies.get(e.id);
    if (copy) return copy.refKey;
    return existing.get(e.id)?.refKey ?? null;
  };
  const label = (key: string): string => {
    const e = byKey.get(key);
    return e ? entityTitle(source, e.id) : key;
  };
  for (const e of copying) {
    const map = fieldOf.get(e.typeId)!;
    const fields: Record<string, FieldValue> = {};
    for (const f of source.types.get(e.typeId)!.fields) {
      const def = map.get(f.key);
      const v = e.fields[f.key];
      if (!def || !filled(v)) continue;
      if (f.type === 'image') {
        const id = typeof v === 'string' ? asset(v) : null;
        if (id) fields[def.key] = id;
      } else if ((f.type === 'text' || f.type === 'longtext') && typeof v === 'string') {
        fields[def.key] = remapRefs(v, to, label);
      } else fields[def.key] = v;
    }
    copies.get(e.id)!.fields = fields;
    // Beziehungen zu ebenfalls übernommenen bzw. schon vorhandenen Elementen bleiben (ohne „ab“/„bis“ – die Szenen kommen nicht mit)
    const relations = (e.relations ?? []).flatMap((r) => {
      const targetId = copies.get(r.targetId)?.id ?? existing.get(r.targetId)?.id;
      return targetId ? [{ id: newId(), kind: r.kind, targetId, ...(r.forms ? { forms: r.forms } : {}) }] : [];
    });
    if (relations.length > 0) copies.get(e.id)!.relations = relations;
  }

  // --- Kategorien: gleichnamige hier, sonst neue; die Karten auf freie Plätze ---
  const groups: LibraryGroup[] = target.story.groups.map((g) => ({ ...g }));
  const layout: PlacedEntity[] = layoutLibrary(target).map((p) => ({ ...p }));
  const person = target.story.types.find((t) => t.builtin && t.name === 'Person')?.id;
  const ofType = (typeId: string): LibraryGroup[] => groups.filter((g) => (g.typeId ?? person) === typeId);
  const created: LibraryGroup[] = [];
  const changed = new Set<string>();
  const moved = new Set<string>();
  const copyById = new Map([...copies.values()].map((c) => [c.id, c]));

  /** je Zielklasse die Kategorien der Quelle samt ihren Einträgen */
  const wanted = new Map<string, Map<string, { group: LibraryGroup; entries: Entity[] }>>();
  for (const e of copying) {
    if (e.x === null || e.y === null) continue;
    const category = categoryOf(groupsOfType(source, e.typeId), { x: e.x, y: e.y });
    if (!category) continue;
    const typeId = typeOf.get(e.typeId)!.id;
    const byName = wanted.get(typeId) ?? new Map<string, { group: LibraryGroup; entries: Entity[] }>();
    wanted.set(typeId, byName);
    const slot = byName.get(norm(category.name)) ?? { group: category, entries: [] };
    slot.entries.push(e);
    byName.set(norm(category.name), slot);
  }
  for (const [typeId, byName] of wanted) {
    // in der Reihenfolge der Quelle (so stehen neue Kategorien auch in der Kartenansicht)
    for (const from of orderedGroups([...byName.values()].map((w) => w.group))) {
      const { entries } = byName.get(norm(from.name))!;
      let group = orderedGroups(ofType(typeId)).find((g) => norm(g.name) === norm(from.name));
      if (!group) {
        const frame = newCategoryFrame(
          ofType(typeId),
          layout.filter((p) => p.placed && p.entity.typeId === typeId),
        );
        group = { id: newId(), typeId, name: from.name, color: from.color, ...frame, h: frameSize(2, Math.ceil(entries.length / 2)).h, rank: groupRankAtEnd(ofType(typeId)) };
        groups.push(group);
        created.push(group);
      }
      const g = group;
      for (const e of entries) {
        const copy = copies.get(e.id)!;
        const all = ofType(typeId);
        const others = all.filter((x) => x.id !== g.id);
        // noch nicht platzierte Karten liegen nie in einer Kategorie (die Pinnwand ordnet sie unter allen Rahmen an)
        const inside = layout.filter((p) => p.placed && p.entity.typeId === typeId && categoryOf(all, p)?.id === g.id);
        const slot = slotInGroup(g, inside, others.filter((x) => x.y < g.y + g.h));
        if (slot.w !== g.w || slot.h !== g.h) {
          // der Rahmen wächst: was darunter liegt, rückt nach (wie beim Hineinlegen in der Bibliothek)
          const shift = growthShift(
            g,
            slot.h,
            others,
            layout.filter((p) => p.entity.typeId === typeId),
          );
          g.w = slot.w;
          g.h = slot.h;
          changed.add(g.id);
          for (const f of shift.frames) {
            groups.find((x) => x.id === f.id)!.y = f.y;
            changed.add(f.id);
          }
          for (const c of shift.cards) {
            layout.find((p) => p.entity.id === c.id)!.y = c.y;
            const own = copyById.get(c.id);
            if (own) own.y = c.y;
            else moved.add(c.id);
          }
        }
        copy.x = slot.x;
        copy.y = slot.y;
        layout.push({ entity: copy, x: slot.x, y: slot.y, placed: true });
      }
    }
  }

  return {
    entities: [...copies.values()],
    skipped,
    types,
    typeFields: [...fieldLists].map(([typeId, fields]) => ({ typeId, fields })),
    groups: created,
    groupChanges: target.story.groups
      .filter((g) => changed.has(g.id))
      .map((g) => {
        const now = groups.find((x) => x.id === g.id)!;
        return { id: g.id, y: now.y, w: now.w, h: now.h };
      }),
    entityMoves: [...moved].map((id) => ({ id, y: layout.find((p) => p.entity.id === id)!.y })),
    assets: [...assetIds].map(([from, to]) => ({ from, to })),
    origins: [...copies].map(([from, c]) => ({ id: c.id, from })),
  };
}
